import React, { useEffect, useState } from 'react'
import LocalHelmet from '../common/LocalHelmet'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import ThreeLayerDiagram from '../components/ThreeLayerDiagram'
import * as FiIcons from 'react-icons/fi'
import commentaryData from '../content/commentaryData.json'
import { trackPageView } from '../utils'

const RAW_BASE = 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main';
const CDN_BASE = 'https://cdn.jsdelivr.net/gh/richardsonstrategygroup/rsg-assets@main';
const toCdn = (u) => u?.replace(RAW_BASE, CDN_BASE);

// normalize any "github.com/.../blob/..." link into a raw URL
const toRaw = (u) =>
  u?.includes('github.com') && u?.includes('/blob/')
    ? u.replace('https://github.com', 'https://raw.githubusercontent.com').replace('/blob/', '/')
    : u;

const { 
  FiArrowLeft, 
  FiClock, 
  FiUser, 
  FiCalendar, 
  FiShare2, 
  FiLinkedin, 
  FiTwitter, 
  FiMail,
  FiArrowRight 
} = FiIcons

const CommentaryPost = () => {
  const { slug } = useParams()
  const [relatedPosts, setRelatedPosts] = useState([])
  
  const allPosts = [...commentaryData.featuredPosts, ...commentaryData.recentPosts]
  const post = allPosts.find(p => p.slug === slug)
  
  useEffect(() => {
    if (post) {
      trackPageView(`/strategic-commentary/${slug}`)
      
      // Get related posts (same category, excluding current post)
      const related = allPosts
        .filter(p => p.id !== post.id && p.category === post.category)
        .slice(0, 3)
      
      // If not enough related posts, add some from other categories
      if (related.length < 3) {
        const additional = allPosts
          .filter(p => p.id !== post.id && !related.includes(p))
          .slice(0, 3 - related.length)
        related.push(...additional)
      }
      
      setRelatedPosts(related)
    }
  }, [slug, post, allPosts])

  if (!post) {
    return (
      <div className="min-h-screen py-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">Article Not Found</h1>
          <Link 
            to="/strategic-commentary"
            className="text-primary-600 hover:text-primary-700 transition-colors"
          >
            ← Back to Strategic Commentary
          </Link>
        </div>
      </div>
    )
  }

  const shareUrl = `${window.location.origin}/strategic-commentary/${slug}`
  const shareText = `${post.title} - Strategic insights from Richardson Strategy Group`

  const handleShare = (platform) => {
    const urls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`
    }
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400')
    }
  }

  // Function to parse markdown-like formatting
  const parseContent = (content) => {
    // Handle bold text **text**
    let parsed = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    
    // Handle images ![alt](url)
    parsed = parsed.replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, url) => {
      return `<img src="${url}" alt="${alt}" class="w-full max-w-3xl mx-auto my-6 rounded-lg shadow-md" loading="lazy" />`
    })
    
    // Handle component placeholders
    parsed = parsed.replace(/<ThreeLayerDiagram \/>/g, '___COMPONENT_PLACEHOLDER___')
    
    return parsed
  }

  // Function to render content with components
  const renderContent = (content) => {
    const parsedContent = parseContent(content)
    const parts = parsedContent.split('___COMPONENT_PLACEHOLDER___')
    
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        <div dangerouslySetInnerHTML={{ __html: part }} />
        {index < parts.length - 1 && <ThreeLayerDiagram />}
      </React.Fragment>
    ))
  }

  // JSON-LD payload for LocalHelmet
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.summary,
    image: toRaw(post.image) || post.image,
    author: {
      '@type': 'Person',
      name: post.author,
      url: 'https://www.linkedin.com/company/richardson-strategy-group'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Richardson Strategy Group',
      logo: {
        '@type': 'ImageObject',
        url: 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main/brand/rsg-logo.svg'
      }
    },
    datePublished: post.publishDate,
    dateModified: post.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://richardsonstrategy.group/strategic-commentary/${post.slug}`
    }
  }

  const absoluteCanonical = `https://richardsonstrategy.group/strategic-commentary/${post.slug}`
  const ogImage = toRaw(post.image) || post.image

  return (
    <>
      {/* HEAD META (rendered only when post exists) */}
      {post && (
        <LocalHelmet prioritizeSeoTags>
          <title>{post.title} | Strategic Commentary | Richardson Strategy Group</title>
          <meta name="description" content={post.summary} />
          <link rel="canonical" href={absoluteCanonical} />

          {/* Open Graph */}
          <meta property="og:site_name" content="Richardson Strategy Group" />
          <meta property="og:type" content="article" />
          <meta
            property="og:title"
            content={`${post.title} | Strategic Commentary | Richardson Strategy Group`}
          />
          <meta property="og:description" content={post.summary} />
          <meta property="og:url" content={absoluteCanonical} />
          {ogImage && <meta property="og:image" content={ogImage} />}

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={`${post.title} | Strategic Commentary | Richardson Strategy Group`}
          />
          <meta name="twitter:description" content={post.summary} />
          {ogImage && <meta name="twitter:image" content={ogImage} />}

          {/* JSON-LD */}
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        </LocalHelmet>
      )}

      <article className="min-h-screen py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Breadcrumb */}
          <nav className="mb-8">
            <Link 
              to="/strategic-commentary"
              className="inline-flex items-center space-x-2 text-secondary-600 hover:text-primary-600 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span>Strategic Commentary</span>
            </Link>
          </nav>

          {/* Series Banner - Renders above hero if present */}
          {post.seriesBannerHtml && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
              dangerouslySetInnerHTML={{ __html: post.seriesBannerHtml }}
            />
          )}

          {/* Article Header */}
          <header className="mb-8 md:mb-12">
            <div className="flex items-center space-x-4 text-sm text-secondary-500 mb-4">
              <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                <span>{new Date(post.publishDate).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-secondary-900 mb-6 leading-tight"
            >
              {post.title}
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex items-center justify-between flex-wrap gap-4"
            >
              <div className="flex items-center space-x-3">
                <img
                  src={commentaryData.author.image}
                  alt={commentaryData.author.name}
                  className="w-10 h-10 rounded-full"
                  loading="eager"
                />
                <div>
                  <div className="font-medium text-secondary-900">{post.author}</div>
                  <div className="text-sm text-secondary-600">{commentaryData.author.title}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-secondary-600 mr-2">Share:</span>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="p-2 text-secondary-600 hover:text-primary-600 transition-colors"
                  aria-label="Share on LinkedIn"
                >
                  <SafeIcon icon={FiLinkedin} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="p-2 text-secondary-600 hover:text-primary-600 transition-colors"
                  aria-label="Share on Twitter"
                >
                  <SafeIcon icon={FiTwitter} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleShare('email')}
                  className="p-2 text-secondary-600 hover:text-primary-600 transition-colors"
                  aria-label="Share via email"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </header>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8 md:mb-12"
          >
            <img
              src={toRaw(post.image)}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl"
              loading="eager"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = toCdn(toRaw(post.image)); }}
            />
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="prose prose-lg max-w-none"
          >
            {/* Intro */}
            <div className="text-xl text-secondary-700 leading-relaxed mb-8 p-6 bg-secondary-50 rounded-xl border-l-4 border-primary-500">
              {renderContent(post.content.intro)}
            </div>

            {/* Content Sections */}
            <div className="space-y-8">
              {post.content.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                    {section.heading}
                  </h2>
                  <div className="text-secondary-700 leading-relaxed space-y-4">
                    {section.content.split('\n').map((paragraph, pIndex) => (
                      <div key={pIndex} className="space-y-4">
                        {renderContent(paragraph)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Conclusion */}
            <div className="mt-8 p-6 bg-primary-50 rounded-xl border border-primary-200">
              <h3 className="text-xl font-bold text-secondary-900 mb-3">Key Takeaway</h3>
              <div className="text-secondary-700 leading-relaxed font-medium">
                {renderContent(post.content.conclusion)}
              </div>
            </div>

            {/* Ok So What Now Section */}
            {post.content.okSoWhatNow && (
              <div className="mt-8 p-6 bg-secondary-50 rounded-xl border border-secondary-200">
                <h3 className="text-xl font-bold text-secondary-900 mb-3">Ok, So What Now?</h3>
                <div className="text-secondary-700 leading-relaxed space-y-4">
                  {post.content.okSoWhatNow.split('\n').map((paragraph, pIndex) => (
                    <div key={pIndex}>
                      {renderContent(paragraph)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Sources */}
          {post.sources && post.sources.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 p-6 bg-white border border-secondary-200 rounded-2xl"
            >
              <h3 className="text-lg font-bold text-secondary-900 mb-4">Sources</h3>
              <div className="space-y-2">
                {post.sources.map((source, index) => (
                  <div key={index} className="text-sm">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      {source.label}
                    </a>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Author Bio */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 p-6 bg-white border border-secondary-200 rounded-2xl"
          >
            <div className="flex items-start space-x-4">
              <img
                src={commentaryData.author.image}
                alt={commentaryData.author.name}
                className="w-16 h-16 rounded-full flex-shrink-0"
                loading="lazy"
              />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-secondary-900 mb-1">
                  {commentaryData.author.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">
                  {commentaryData.author.title}
                </p>
                <p className="text-secondary-600 leading-relaxed text-sm">
                  {commentaryData.author.bio}
                </p>
                <a
                  href={commentaryData.author.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 mt-3 text-primary-600 hover:text-primary-700 transition-colors text-sm font-medium"
                >
                  <SafeIcon icon={FiLinkedin} className="w-4 h-4" />
                  <span>Connect on LinkedIn</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Service CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 p-6 md:p-8 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl text-center"
          >
            <h3 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4">
              Facing Similar Strategic Challenges?
            </h3>
            <p className="text-secondary-600 mb-6 max-w-2xl mx-auto">
              This analysis comes from real client work. If your organization is navigating 
              similar growth strategy decisions, let's discuss how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-flex items-center justify-center space-x-2"
              >
                <span>Schedule a Conversation</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="border border-secondary-300 text-secondary-700 px-6 py-3 rounded-lg font-medium hover:bg-white transition-colors"
              >
                Explore Our Services
              </Link>
            </div>
          </motion.div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-16"
            >
              <h3 className="text-2xl font-bold text-secondary-900 mb-8">Related Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <article
                    key={relatedPost.id}
                    className="bg-white border border-secondary-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                    <div className="p-4">
                      <div className="text-xs text-secondary-500 mb-2">
                        {relatedPost.category} • {relatedPost.readTime}
                      </div>
                      <h4 className="font-bold text-secondary-900 mb-2 text-sm leading-tight">
                        <Link 
                          to={`/strategic-commentary/${relatedPost.slug}`}
                          className="hover:text-primary-600 transition-colors"
                        >
                          {relatedPost.title}
                        </Link>
                      </h4>
                      <p className="text-secondary-600 text-xs leading-relaxed">
                        {relatedPost.summary.substring(0, 100)}...
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </motion.div>
          )}

        </div>
      </article>
    </>
  )
}

export default CommentaryPost